/**
 * useRoomDevices (web) — changer de micro, de caméra ou de haut-parleur en pleine séance.
 *
 * Le sélecteur de l'ancienne barre (`TrackToggleWithDeviceSelectM` → `MediaDeviceSelectM`) : la
 * sélection de LiveKit (`useMediaDeviceSelect`) sur la salle, pour les trois genres, les entrées
 * « système » retirées. Un appareil qui ne se prend pas part dans Bugsnag.
 *
 * Un écart voulu : le micro et la caméra choisis entrent aussi dans le choix du test d'avant séance
 * (`liveMode.precallDevices`). La salle le réapplique à chaque connexion (`PrecallDeviceSync`) :
 * sans ça, une reconnexion complète rendait le périphérique du test, et l'élève perdait son choix
 * (l'ancien avait ce défaut). Le choix vaut le temps du live, comme celui du test.
 */
import { useMediaDeviceSelect, useRoomContext } from "@livekit/components-react"
import { type Room } from "livekit-client"
import { useCallback, useMemo, useRef } from "react"
import { useDispatch } from "react-redux"

import { setPrecallDevices } from "../../../data/redux/actions/liveMode"
import { type EngineTelemetry } from "../../shared/telemetry"
import {
    activeRealDeviceId,
    withoutSystemAliases,
    type RoomDeviceChoice,
    type RoomDeviceKind,
    type RoomDevices,
} from "./roomDevices"

function useChoice(
    kind: RoomDeviceKind,
    room: Room,
    telemetry: EngineTelemetry,
    remember: (kind: RoomDeviceKind, id: string) => void
): RoomDeviceChoice {
    // Le sélecteur de LiveKit se réabonne à chaque `onError` neuf (sa liste repart de zéro et il
    // réénumère les appareils) : une flèche recréée à chaque rendu faisait redessiner le live sans fin.
    const telemetryRef = useRef(telemetry)
    telemetryRef.current = telemetry
    const onError = useCallback(
        (error: Error) => {
            telemetryRef.current.error("device_select", error, { kind })
        },
        [kind]
    )
    // Les droits sont demandés au test d'avant séance : ici, un appareil sans libellé ne relance rien.
    const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
        kind,
        room,
        onError,
        requestPermissions: false,
    })
    return useMemo(
        () => ({
            devices: withoutSystemAliases(devices).map((device) => ({ id: device.deviceId, label: device.label })),
            activeId: activeRealDeviceId(devices, activeDeviceId),
            select: (id: string) => {
                setActiveMediaDevice(id)
                    .then(() => remember(kind, id))
                    .catch((cause: unknown) => {
                        telemetry.error("device_switch", cause, { kind })
                    })
            },
        }),
        [devices, activeDeviceId, setActiveMediaDevice, telemetry, kind, remember]
    )
}

export function useRoomDevices(telemetry: EngineTelemetry): RoomDevices | null {
    const room = useRoomContext() as Room
    const dispatch = useDispatch()
    const remember = useMemo(
        () => (kind: RoomDeviceKind, id: string) => {
            // La sortie du son ne se rejoue pas à la connexion : seuls le micro et la caméra se gardent.
            if (kind !== "audiooutput") dispatch(setPrecallDevices({ [kind]: id }) as never)
        },
        [dispatch]
    )
    const microphone = useChoice("audioinput", room, telemetry, remember)
    const camera = useChoice("videoinput", room, telemetry, remember)
    const speaker = useChoice("audiooutput", room, telemetry, remember)
    return useMemo(() => ({ microphone, camera, speaker }), [microphone, camera, speaker])
}
